import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import {
  Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon,
  Link2, List, ListOrdered, Palette, Eraser
} from "lucide-react";

const PALETTE = [
  "#0B3C5D", "#F58220", "#0F172A", "#475569", "#94A3B8",
  "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#FFFFFF",
];

export default function RichEditor({
  value,
  onChange,
  placeholder = "Type here…",
  singleLine = false,
  minHeight = 38,
  className = "",
  testId,
  editorRef,
  onFocus,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[#F58220] underline" } },
      }),
      TextStyle,
      Color,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-testid": testId || "rich-editor",
        class: `tiptap-content focus:outline-none px-3 py-2 rounded-md border border-slate-200 bg-white ${singleLine ? "single-line" : ""}`,
        style: `min-height:${minHeight}px;`,
        "aria-placeholder": placeholder,
      },
      handleKeyDown: (_view, e) => {
        if (singleLine && e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          return true;
        }
        return false;
      },
    },
    onFocus: () => { if (onFocus) onFocus(); },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap returns "<p></p>" when empty — normalize to ""
      const empty = html === "<p></p>" || html === "";
      onChange(empty ? "" : html);
    },
  });

  // Expose editor instance via ref
  useEffect(() => {
    if (editorRef) editorRef.current = editor || null;
    return () => { if (editorRef && editorRef.current === editor) editorRef.current = null; };
  }, [editor, editorRef]);

  // Keep editor in sync if external value changes (e.g., switching surveys)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if ((incoming === "" && current === "<p></p>") || incoming === current) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`rich-editor ${className}`} data-testid={`${testId || "rich-editor"}-wrap`}>
      <Toolbar editor={editor} testId={testId}/>
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor, testId }) {
  const [colorOpen, setColorOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setColorOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const btn = (active) =>
    `h-7 w-7 grid place-items-center rounded text-slate-600 hover:bg-slate-100 transition ${active ? "bg-[#FFF0DF] text-[#1F3A6B]" : ""}`;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 border border-slate-200 border-b-0 rounded-t-md bg-slate-50/70 px-1.5 py-1" data-testid={`${testId || "rich"}-toolbar`}>
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} title="Bold (⌘B)" data-testid={`${testId || "rich"}-bold`}>
        <BoldIcon size={13}/>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} title="Italic (⌘I)" data-testid={`${testId || "rich"}-italic`}>
        <ItalicIcon size={13}/>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive("underline"))} title="Underline (⌘U)" data-testid={`${testId || "rich"}-underline`}>
        <UnderlineIcon size={13}/>
      </button>
      <span className="w-px h-4 bg-slate-200 mx-1"/>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} title="Bullet list" data-testid={`${testId || "rich"}-ul`}>
        <List size={13}/>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} title="Numbered list" data-testid={`${testId || "rich"}-ol`}>
        <ListOrdered size={13}/>
      </button>
      <span className="w-px h-4 bg-slate-200 mx-1"/>
      <button type="button" onClick={promptLink} className={btn(editor.isActive("link"))} title="Add / edit link" data-testid={`${testId || "rich"}-link`}>
        <Link2 size={13}/>
      </button>
      <div className="relative" ref={wrapRef}>
        <button type="button" onClick={() => setColorOpen((o) => !o)} className={btn(false)} title="Text color" data-testid={`${testId || "rich"}-color`}>
          <Palette size={13}/>
        </button>
        {colorOpen && (
          <div className="absolute z-30 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-elev-2 p-2 w-44 grid grid-cols-6 gap-1.5">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }}
                className="w-6 h-6 rounded-full border border-slate-200 hover:scale-110 transition"
                style={{ backgroundColor: c }}
                title={c}
                data-testid={`${testId || "rich"}-color-${c.replace("#", "")}`}
              />
            ))}
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }}
              className="col-span-6 mt-1 text-[11px] text-slate-600 hover:text-[#1F3A6B] flex items-center justify-center gap-1 py-1 rounded hover:bg-slate-50"
            >
              <Eraser size={11}/> Reset color
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
